import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getProbeSummary,
  updateProbe,
  getLatestProbeData,
  getProbeData,
} from "api/ProbeAPI";
import { AppThunk } from "app/store";
import { RootState } from "app/rootReducer";
import { getProbeIds } from "../hub/hubSlice";
import { ProbeDataGraphType } from "types/enums";
import moment from "moment";

interface ProbeState {
  loading: boolean;
  errorMessage: string | undefined;
  probes: Array<common.Probe> | undefined;
  graphSelectionType: ProbeDataGraphType;
}

const initialState: ProbeState = {
  loading: false,
  errorMessage: undefined,
  probes: undefined,
  graphSelectionType: ProbeDataGraphType.last24HoursAverage,
};

const probe = createSlice({
  name: "probe",
  initialState,
  reducers: {
    getProbeSummaryPending(state: ProbeState) {
      state.loading = true;
    },
    getProbeSummarySuccess(state, action: PayloadAction<Array<common.Probe>>) {
      state.loading = false;
      state.probes = action.payload;
    },
    getProbeSummaryFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.probes = undefined;
      state.errorMessage = action.payload;
    },
    updateProbePending(state) {
      state.loading = true;
    },
    updateProbeSuccess(state, action: PayloadAction<Array<common.Probe>>) {
      const updatedProbe = action.payload;

      state.loading = false;
      // state.probes = state.probes.map((probe: common.Probe) => {
      //   if (updatedProbe.id === probe.id) return updatedProbe;
      //   return probe;
      // });
      state.probes = updatedProbe;
    },
    updateProbeFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    updateProbeData(state, action: PayloadAction<common.Probe>) {
      return {
        ...state,
        probes: state.probes.map((x) => {
          if (x.id === action.payload.id) {
            return action.payload;
          } else {
            return x;
          }
        }),
        loading: false,
      };
    },
    updateProbeTemperature(
      state,
      action: PayloadAction<{ deviceID: string; latestTemperature: number }>
    ) {
      return {
        ...state,
        probes: state.probes.map((x) => {
          if (x.deviceID === action.payload.deviceID) {
            return {
              ...x,
              latestTemperature: action.payload.latestTemperature,
              latestDateTime: moment.utc().format(),
            };
          } else {
            return x;
          }
        }),
        loading: false,
      };
    },
    clearError(state) {
      state.errorMessage = undefined;
    },
    changeProbeDataGraphType(state, action: PayloadAction<ProbeDataGraphType>) {
      state.loading = true;
      state.graphSelectionType = action.payload;
    },
  },
});

export const {
  getProbeSummaryPending,
  getProbeSummarySuccess,
  getProbeSummaryFailed,
  updateProbePending,
  updateProbeSuccess,
  updateProbeFailed,
  updateProbeData,
  clearError,
  changeProbeDataGraphType,
  updateProbeTemperature,
} = probe.actions;

/**
 * Selectors
 */
export const getProbes = (state: RootState) => state.probe.probes;
export const getProbe = (state: RootState, probeId: number) =>
  state?.probe?.probes?.find((x) => x.id === probeId);
export const isLoading = (state: RootState) => state.probe.loading;
export const getError = (state: RootState) => state.probe.errorMessage;
export const getSelectedProbeDataGraphType = (
  state: RootState
): ProbeDataGraphType => state.probe.graphSelectionType;

export default probe.reducer;

// Thunks
export const getProbeDataThunk = (
  probeId: number,
  probeDataGraphType: ProbeDataGraphType
): AppThunk => async (dispatch: any, getState: any) => {
  dispatch(getProbeSummaryPending());

  const state = getState();
  try {
    const probeData = await getProbeData(probeId, probeDataGraphType);
    const probe = { ...getProbe(state, probeId), probeData: probeData };

    dispatch(updateProbeData(probe));
  } catch (e) {
    dispatch(getProbeSummaryFailed(e));
  }
};

export const getProbeSummaryThunk = (
  probeIds: Array<number>
): AppThunk => async (dispatch: any, getState: any) => {
  dispatch(getProbeSummaryPending());
  const state = getState();

  try {
    const probeSummary = await getProbeSummary(probeIds);
    dispatch(getProbeSummarySuccess(probeSummary));
  } catch (ex) {
    dispatch(getProbeSummaryFailed(ex));
  }
};

/**
 * Update the probe
 * @param probe The updates probe
 */
export const updateProbeThunk = (probe: common.Probe): AppThunk => async (
  dispatch: any
) => {
  dispatch(updateProbePending());

  updateProbe(probe)
    .then((response) => {
      dispatch(updateProbeSuccess(response));
    })
    .catch((e) => {
      dispatch(
        updateProbeFailed(
          "An error occurred, we were unable to update the probe. Please try again or contact us if it persists."
        )
      );
    });
};

export const updateSelectedProbeDataGraphType = (
  probeDataGraphType: ProbeDataGraphType
): AppThunk => async (dispatch: any) => {
  dispatch(changeProbeDataGraphType(probeDataGraphType));
};
